import { useState } from "react";
import { Bubble, Sender } from "@ant-design/x";
import { Card } from "@rumbledirect/ui";
import { Flex, Spin, theme, Typography } from "antd";
import { sendMessage } from "../../../../lib/api/resources/agent";
import { parseReply } from "../lib/parseReply";
import markdownit from "markdown-it";
import { useMutation, useQuery } from "@tanstack/react-query";

interface Message {
  role: "user" | "assistant";
  content: string;
  loading?: boolean;
  typing?: boolean;
}

const AGENT_ID = "00000000-0000-7000-8000-001000000004";

const md = markdownit({ html: true, breaks: true });

const renderMarkdown = (content: string, role: "user" | "assistant") => {
  return (
    <Typography>
      <div
        style={{ color: role === "user" ? "rgb(255, 79, 20)" : "#000000" }}
        dangerouslySetInnerHTML={{ __html: md.render(content) }}
      />
    </Typography>
  );
};

export const AgentDossierKnowledgeBaseTab = () => {
  const { token } = theme.useToken();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [senderValue, setSenderValue] = useState<string>("");

  // Initialize conversation with TanStack Query
  const { isLoading: isInitializing } = useQuery({
    queryKey: ["agentSession", "initial"],
    queryFn: async () => {
      const response = await sendMessage("", AGENT_ID, null);
      const newSessionId = response.headers["x-session-id"];
      const message = parseReply(response);

      setSessionId(newSessionId);
      setMessages([{ role: "assistant", content: message }]);

      return { sessionId: newSessionId, message };
    },
    enabled: !sessionId,
    staleTime: Infinity,
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async (content: string) => {
      const response = await sendMessage(content, AGENT_ID, sessionId);
      return parseReply(response);
    },
    onMutate: async (content: string) => {
      // Optimistically add user message
      setSenderValue("");
      setMessages((prev) => [...prev, { role: "user", content }]);
      // Add loading placeholder for assistant response
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "", loading: true },
      ]);
    },
    onSuccess: (reply) => {
      // Replace loading message with actual reply with typing effect
      setMessages((prev) => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = {
          role: "assistant",
          content: reply,
          loading: false,
          typing: true,
        };
        return newMessages;
      });
    },
    onError: () => {
      // Remove both the optimistic user message and loading placeholder on error
      setMessages((prev) => prev.slice(0, -2));
      setSenderValue(senderValue); // Restore the message in sender
    },
  });

  const handleSendMessage = (content: string) => {
    if (!content.trim() || !sessionId) return;
    sendMessageMutation.mutate(content);
  };

  return (
    <Card title="Pet van Schadenet">
      <Spin spinning={isInitializing}>
        <div style={{ height: "calc(100vh - 325px)" }}>
          <Flex vertical justify="space-between" style={{ height: "100%" }}>
            <Bubble.List
              autoScroll
              items={messages.map((message, index) => ({
                key: index,
                messageRender: (content) =>
                  renderMarkdown(content, message.role),
                content: message.content,
                placement: message.role === "user" ? "end" : "start",
                loading: message.loading,
                typing: message.typing ? { step: 5, interval: 20 } : undefined,
                styles:
                  message.role === "user"
                    ? {
                        content: {
                          backgroundColor: "#fff0e6",
                          paddingBottom: 8,
                        },
                      }
                    : {
                        content: {
                          paddingBottom: 8,
                        },
                      },
              }))}
            />
            <Sender
              style={{ marginTop: token.marginMD }}
              value={senderValue}
              onChange={setSenderValue}
              onSubmit={handleSendMessage}
              loading={sendMessageMutation.isPending}
              disabled={!sessionId || isInitializing}
              placeholder="Stel een vraag..."
            />
          </Flex>
        </div>
      </Spin>
    </Card>
  );
};
